// ============================================
// Media Type Constants
// ============================================

export const MEDIA_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
} as const;

export type MediaType = (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];

// ============================================
// Supported Formats
// ============================================

export const SUPPORTED_FORMATS = {
  IMAGE: ["jpg", "jpeg", "png"],
  VIDEO: ["mp4", "3gp"],
  DOCUMENT: ["txt", "xls", "xlsx", "doc", "docx", "ppt", "pptx", "pdf", "csv"],
} as const;

// MIME type mappings for each format
export const MIME_TYPES = {
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  // Videos
  mp4: "video/mp4",
  "3gp": "video/3gpp",
  // Documents
  txt: "text/plain",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  pdf: "application/pdf",
  csv: "text/csv",
} as const;

// ============================================
// File Size Limits (in MB)
// ============================================

export const MEDIA_UPLOAD_MAX_FILE_SIZE = {
  IMAGE: 5,
  VIDEO: 16,
  DOCUMENT: 50,
} as const;

// Convert MB to bytes
export const MEDIA_UPLOAD_MAX_FILE_SIZE_BYTES = {
  IMAGE: MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE * 1024 * 1024,
  VIDEO: MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO * 1024 * 1024,
  DOCUMENT: MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT * 1024 * 1024,
} as const;

// ============================================
// Storage Upload Types
// ============================================

// Upload Request Types
export interface StorageUploadRequest {
  file: File;
  cin: string;
}

export interface StorageUploadWithNameRequest extends StorageUploadRequest {
  customFileName: string;
}

// Upload Response Types (API response structure)
export interface StorageUploadApiResponse {
  status: boolean;
  message: string;
  data?: {
    fileLink: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
  };
  // Alternative flat response structure
  fileLink?: string;
  fileName?: string;
}

// Parsed upload result for convenience
export interface StorageUploadResult {
  fileUrl: string;
  fileName: string;
}

// ============================================
// Storage Delete Types
// ============================================

// Delete Request Types
export interface StorageDeleteRequest {
  cin: string;
  fileName: string;
}

// Delete Response Types (API response structure)
export interface StorageDeleteApiResponse {
  status: boolean;
  message: string;
  data?: {
    deleted: boolean;
    fileName?: string;
  };
}

// Parsed delete result for convenience
export interface StorageDeleteResult {
  success: boolean;
  message: string;
}

// ============================================
// Error Types
// ============================================

export interface StorageOperationError {
  status: number | string;
  message: string;
  code?: string;
}

// ============================================
// Validation Types & Helpers
// ============================================

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  mediaType?: MediaType;
}

// Helper to get file extension
export const getFileExtension = (fileName: string): string => {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

// Helper to determine media type from file extension
export const getMediaTypeFromExtension = (extension: string): MediaType | null => {
  const ext = extension.toLowerCase();
  if (SUPPORTED_FORMATS.IMAGE.includes(ext as typeof SUPPORTED_FORMATS.IMAGE[number])) {
    return MEDIA_TYPE.IMAGE;
  }
  if (SUPPORTED_FORMATS.VIDEO.includes(ext as typeof SUPPORTED_FORMATS.VIDEO[number])) {
    return MEDIA_TYPE.VIDEO;
  }
  if (SUPPORTED_FORMATS.DOCUMENT.includes(ext as typeof SUPPORTED_FORMATS.DOCUMENT[number])) {
    return MEDIA_TYPE.DOCUMENT;
  }
  return null;
};

// Helper to get max file size for a media type
export const getMaxFileSizeBytes = (mediaType: MediaType): number => {
  switch (mediaType) {
    case MEDIA_TYPE.IMAGE:
      return MEDIA_UPLOAD_MAX_FILE_SIZE_BYTES.IMAGE;
    case MEDIA_TYPE.VIDEO:
      return MEDIA_UPLOAD_MAX_FILE_SIZE_BYTES.VIDEO;
    case MEDIA_TYPE.DOCUMENT:
      return MEDIA_UPLOAD_MAX_FILE_SIZE_BYTES.DOCUMENT;
    default:
      return MEDIA_UPLOAD_MAX_FILE_SIZE_BYTES.DOCUMENT;
  }
};

// Helper to get max file size in MB for a media type
export const getMaxFileSizeMB = (mediaType: MediaType): number => {
  switch (mediaType) {
    case MEDIA_TYPE.IMAGE:
      return MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE;
    case MEDIA_TYPE.VIDEO:
      return MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO;
    case MEDIA_TYPE.DOCUMENT:
      return MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT;
    default:
      return MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT;
  }
};

// Get all supported extensions as a flat array
export const getAllSupportedExtensions = (): string[] => [
  ...SUPPORTED_FORMATS.IMAGE,
  ...SUPPORTED_FORMATS.VIDEO,
  ...SUPPORTED_FORMATS.DOCUMENT,
];

// Get all supported MIME types
export const getAllSupportedMimeTypes = (): string[] => Object.values(MIME_TYPES);

/**
 * Extract filename from a storage URL
 * Handles URLs like: https://notifyydevobjstorage02.blob.core.windows.net/.../filename.ext
 * @param url - The full URL of the file
 * @returns The filename or null if extraction fails
 */
export const extractFilenameFromUrl = (url: string | null | undefined): string | null => {
  if (!url || typeof url !== 'string') return null;
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    const filename = pathParts[pathParts.length - 1];
    if (filename && filename.includes('.')) {
      return decodeURIComponent(filename);
    }
    return null;
  } catch {
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.includes('.')) {
      return lastPart.split('?')[0];
    }
    return null;
  }
};
